import type {
  AdminApiPlaybook,
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type {
  MultiSelectOption,
  MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import { useMemo, useState } from "react";
import { useUpdateTeammateMutation } from "../../../api/entities/teammate.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import type { PerformanceMetricColumnOption } from "../performanceMetricColumns.js";

interface UsePerformanceMetricColumnSelectorOptions {
  /** The playbook the table is currently scoped to. */
  playbook: AdminApiPlaybook | undefined;
  view?: PlaybookView;
  /**
   * Metric columns eligible for display on the current page. Built by
   * `buildPerformanceMetricColumnOptions` from the same gating inputs
   * (outcomeDefinition, feature flags) the table uses, so the menu only
   * surfaces columns the table can actually render.
   */
  availableOptions: readonly PerformanceMetricColumnOption[];
  /** Columns from another surface sharing these preferences, preserved on save. */
  unavailableColumnIds?: readonly string[];
}

interface UsePerformanceMetricColumnSelectorReturn {
  /**
   * Set of toggleable metric column IDs the user has chosen to display.
   * Wired into `buildPerformanceColumns({ selectedMetricColumnIds })` and
   * the table's per-row Calls column gate.
   */
  selectedMetricColumnIds: ReadonlySet<string>;
  /** Menu sections to combine into the unified display-columns dropdown. */
  selectSections: readonly MultiSelectSection[];
  /** Currently-selected option IDs in the combined dropdown. */
  selectedIds: readonly string[];
  /** Current selection plus shared defaults for columns unavailable here. */
  defaultSelectedIds: readonly string[];
  /** Selection-change handler for the combined dropdown. */
  onSelectionChange: (keys: readonly string[]) => void;
}

/**
 * Tracks which performance metric columns are visible in Calls and Team
 * tables. Mirrors `useSectionColumnSelector`: persists per
 * (teammate, playbook) to `teammate.settings.preferences`, resets when
 * the teammate or playbook changes, and exposes composable hooks for
 * the unified display-columns menu.
 */
export function usePerformanceMetricColumnSelector(
  options: UsePerformanceMetricColumnSelectorOptions,
): UsePerformanceMetricColumnSelectorReturn {
  const currentTeammate = useAppSelector(selectTeammate);
  const teammateId = currentTeammate?.id;
  const [updateTeammate] = useUpdateTeammateMutation();

  const {
    playbook,
    availableOptions,
    view = "team",
    unavailableColumnIds = [],
  } = options;
  const preferenceKey =
    view === "calls"
      ? "calls_metric_column_ids"
      : "team_performance_metric_column_ids";
  const playbookId = playbook?.id;

  const availableIds = useMemo(
    () => availableOptions.map((option) => option.id),
    [availableOptions],
  );

  const defaultMetricIds = playbook?.view_defaults?.[view]?.metric_column_ids;
  const defaultsKey = JSON.stringify([view, defaultMetricIds]);

  // Build the persisted selection. `null` / missing entry means "use the
  // default" (every eligible column visible). Unknown / stale IDs are
  // filtered out so removing a column from the codebase doesn't leave
  // dead entries in the menu.
  const initialSelectedIds = useMemo(() => {
    if (!currentTeammate || !playbookId) {
      return availableIds;
    }

    const savedIds =
      currentTeammate.settings.preferences[preferenceKey]?.[playbookId] ??
      defaultMetricIds;

    if (!savedIds) {
      return availableIds;
    }

    const availableIdSet = new Set(availableIds);
    return savedIds.filter((id) => availableIdSet.has(id));
  }, [
    availableIds,
    currentTeammate,
    playbookId,
    defaultMetricIds,
    preferenceKey,
  ]);

  const [selectedIds, setSelectedIds] =
    useState<readonly string[]>(initialSelectedIds);
  const [initializedParams, setInitializedParams] = useState<{
    teammateId: TeammateId | undefined;
    playbookId: PlaybookId | undefined;
    availableIdsKey: string;
    defaultsKey: string | undefined;
  }>({
    teammateId,
    playbookId,
    defaultsKey,
    availableIdsKey: availableIds.join("|"),
  });

  const availableIdsKey = availableIds.join("|");

  // Reinitialize when the persistence context (teammate, playbook) or
  // the set of available options changes — e.g. a playbook gains an
  // outcome definition mid-session.
  if (
    initializedParams.teammateId !== teammateId ||
    initializedParams.playbookId !== playbookId ||
    initializedParams.availableIdsKey !== availableIdsKey ||
    initializedParams.defaultsKey !== defaultsKey
  ) {
    setSelectedIds(initialSelectedIds);
    setInitializedParams({
      teammateId,
      playbookId,
      availableIdsKey,
      defaultsKey,
    });
  }

  const selectedMetricColumnIds = useMemo(
    () => new Set<string>(selectedIds),
    [selectedIds],
  );

  const handleSelectionChange = async (keys: readonly string[]) => {
    setSelectedIds(keys);

    if (!currentTeammate || !playbookId) {
      return;
    }

    try {
      const currentPreferences = currentTeammate.settings.preferences;
      const previousIds =
        currentPreferences[preferenceKey]?.[playbookId] ?? defaultMetricIds;
      await updateTeammate({
        id: currentTeammate.id,
        patchPayload: {
          preferences: {
            [preferenceKey]: {
              ...currentPreferences[preferenceKey],
              [playbookId]: [
                ...keys,
                ...unavailableColumnIds.filter(
                  (id) => previousIds?.includes(id) ?? true,
                ),
              ],
            },
          },
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to save metric column preferences:", error);
    }
  };

  const selectSections = useMemo<readonly MultiSelectSection[]>(() => {
    if (availableOptions.length === 0) {
      return [];
    }
    const optionsForSection: MultiSelectOption[] = availableOptions.map(
      (option) => ({
        id: option.id,
        label: option.label,
      }),
    );
    return [
      {
        id: "performance_metrics",
        label: "Performance metrics",
        options: optionsForSection,
      },
    ];
  }, [availableOptions]);

  return {
    selectedMetricColumnIds,
    selectSections,
    selectedIds,
    defaultSelectedIds: [
      ...selectedIds,
      ...unavailableColumnIds.filter(
        (id) => defaultMetricIds?.includes(id) ?? true,
      ),
    ],
    onSelectionChange: handleSelectionChange,
  };
}
